import { backendApi } from "./backendApi.rtkq";

export interface UsageSummary {
  totalRequests: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCredits: number;
  totalCostUsd: number;
  activeUsers: number;
  activeOffices: number;
}

export interface ModelUsage {
  providerName: string;
  modelName: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  percentage: number;
}

export interface OfficeUsage {
  officeId: string;
  officeName: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  credits: number;
  costUsd: number;
}

export interface UserUsage {
  userId: string;
  email: string;
  displayName: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface OfficeSpend {
  officeId: string;
  officeName: string;
  planName: string;
  spentUsd: number;
  budgetUsd: number | null;
  percentage: number | null;
}

interface DateRange {
  from?: string;
  to?: string;
}

export interface McpUsageFilters extends DateRange {
  email?: string;
  officeId?: string;
  toolName?: string;
}

export interface McpToolUsage {
  userId: string;
  email: string;
  displayName: string;
  officeId: string;
  officeName: string;
  toolName: string;
  calls: number;
  creditsConsumed: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  firstUsedAt: string;
  lastUsedAt: string;
}

export interface McpUsageSummary {
  totals: {
    calls: number;
    creditsConsumed: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  };
  rows: McpToolUsage[];
}

export interface McpUsageLog {
  id: string;
  createdAt: string;
  userId: string;
  email: string;
  displayName: string;
  officeId: string;
  officeName: string;
  toolName: string;
  creditsConsumed: number;
  units: number;
  billingVersion?: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  operationId?: string;
}

export interface McpUsageLogsResponse {
  rows: McpUsageLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const appendUsageFilters = (params: URLSearchParams, filters: McpUsageFilters) => {
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.email) params.set("email", filters.email);
  if (filters.officeId) params.set("officeId", filters.officeId);
  if (filters.toolName) params.set("toolName", filters.toolName);
};

export const usageAnalyticsApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsageSummary: builder.query<UsageSummary, DateRange>({
      query: ({ from, to } = {}) => {
        const params = new URLSearchParams();
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        return `/usage/admin/summary?${params}`;
      },
    }),

    getUsageByModel: builder.query<ModelUsage[], DateRange>({
      query: ({ from, to } = {}) => {
        const params = new URLSearchParams();
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        return `/usage/admin/by-model?${params}`;
      },
    }),

    getUsageByOffice: builder.query<OfficeUsage[], DateRange>({
      query: ({ from, to } = {}) => {
        const params = new URLSearchParams();
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        return `/usage/admin/by-office?${params}`;
      },
    }),

    getUsageByUser: builder.query<UserUsage[], DateRange & { officeId?: string }>({
      query: ({ from, to, officeId } = {}) => {
        const params = new URLSearchParams();
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        if (officeId) params.set("officeId", officeId);
        return `/usage/admin/by-user?${params}`;
      },
    }),

    getSpendByOffice: builder.query<OfficeSpend[], void>({
      query: () => `/usage/admin/spend-by-office`,
    }),

    getMcpUsageSummary: builder.query<McpUsageSummary, McpUsageFilters>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        appendUsageFilters(params, filters);
        return `/usage/admin/mcp/summary?${params}`;
      },
    }),

    getMcpUsageLogs: builder.query<McpUsageLogsResponse, McpUsageFilters & { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 50, ...filters } = {}) => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        appendUsageFilters(params, filters);
        return `/usage/admin/mcp/logs?${params}`;
      },
    }),
  }),
});

export const {
  useGetUsageSummaryQuery,
  useGetUsageByModelQuery,
  useGetUsageByOfficeQuery,
  useGetUsageByUserQuery,
  useGetSpendByOfficeQuery,
  useGetMcpUsageSummaryQuery,
  useGetMcpUsageLogsQuery,
} = usageAnalyticsApi;
